<template>
  <div class="site">
    <div class="notifications">
      <div
        v-for="(content, index) in notifications"
        :key="`notification-${index}`"
        class="notification"
      >
      {{ content }}
      <span
        class="x"
        @click="removeNotification(content)"
      >X</span>
      </div>
    </div>
    <div class="side composition">
      <main>
        <article
          @mouseenter="changeVisual('automation-surveillance.mp4')"
        >
          <aside>
            <div class="source">
              <video :src="`${base}material/clickfarm-03.mp4`" muted loop autoplay></video>
              <p>Phenomenon of group control systems (2020)</p>
            </div>
            <div class="source">
              <img :src="`${base}material/gilbreth-1.jpg`">
              <p>Gilbreth, L. and Gilbreth, F., <a href="https://archive.org/details/OriginalFilm">Time and motion study</a> (~1910)</p>
            </div>
            <div class="source">
              <img :src="`${base}material/framebreaking.jpg`">
              <img :src="`${base}material/office-space.jpg`">
              <p><a href="https://en.wikipedia.org/wiki/Destruction_of_Stocking_Frames,_etc._Act_1812">Destruction of Stocking Frames, etc.</a> (1812), <a href="https://www.imdb.com/title/tt0151804">Office Space</a> (1999)</p>
            </div>
          </aside>
          <div class="content">
            <div class="info">
              <small>25.01.2021</small>
              <small>PART I, PART II</small>
              <small>ABSTRACT</small>
            </div>
            <h2 class="f">Automation asymmetry and it's hidden computational demographics</h2>
            <div class="text">
              <p>The premise of automation and a post-work future seems irresistible. While the automation discourse steers in the direction of technological inevitability and the replacement of human work, its underlying infrastructures expose a very different picture. So advertized, fully autonomous robots are in fact remote controlled by humans, wether it be navigating by setting waypoints or fullfilling tactile excersices through webcam feeds; the industry behind Artifical Intelligence and machine learning is backed by fleets of precarious working conditions, humans being payed negligible amounts to prepare the necessary datasets; and whole industrial halls are filled with innumerous amounts of smartphones to imitate user behaviour and to fake engagement.</p>
                <p>The narrative of automation's technocratic fantasies hides the human and their labour behind a precisely positioned technological determinism and tries to draw them obsolete. It actively challenges the value and agency of work and reinforces imperatives of control.
                Automation became an ideology and with it its surveillance and precarious structures sweeped into every so polished interface: A critique of design's position within the automation discourse becomes necessary.</p>
                <p>Part I
                Within this ideology the given interactions become ever more ambiguous and hard to grasp. Perceptions of creator and user blur continously and questions of the arrangement of useable actors within automation's structures need to be reasked.
                This part looks at the reorganization and displacement of actors within the instructions of automation and explores what it means to be automated.</p>

                <p>Part II
                Within these structures the locality of this vast global ideology become ever more apparent. While labour implications of automation span across spaces, locations and platforms, the localization of concrete precarious and ephemeral places need to be examined.
                This part explores the (post-)industrial infrastructures and the sociomateriality of workplaces and reveals underlying power relations within.</p>
            </div>
          </div>
        </article>
        <article
          @mouseenter="changeVisual('interface-ideologies-user-constructs.mp4')"
        >
          <aside>
            <div class="source">
              <img :src="`${base}material/amazon-rugeley.jpg`">
              <p><a href="https://www.benrobertsphotography.com/work/amazon-unpacked/">Amazon fulfillment center</a>, Rugeley (2013)</p>
            </div>
            <div class="source">
              <img :src="`${base}material/moores-law.png`">
              <p><a href="https://en.wikipedia.org/wiki/Moore%27s_law">Moore's law</a> (1965)</p>
            </div>
            <div class="source">
              <img :src="`${base}material/t-1.jpg`">
              <img :src="`${base}material/t-2.jpg`">
              <p>Transistor sizes <a href="https://www.cedmagic.com/history/transistor-1947.html">5 cm</a> (1947) vs. <a href="https://en.wikipedia.org/wiki/5_nm_process">5 nm</a> (2020)</p>
            </div>
            <div class="source">
              <img :src="`${base}material/immateriaux.jpg`">
              <p>Exhibition <a href="http://socks-studio.com/2014/07/16/les-immateriaux-an-exhibition-of-jean-francois-lyotard-at-the-centre-pompidou-1985/">Les Immatériaux</a> (1985)</p>
            </div>
          </aside>
          <div class="content">
            <div class="info">
              <small>31.08.2020</small>
              <small>part 0</small>
              <small>essay</small>
            </div>
            <h2 class="f">Constant inhabitation of conflicting spaces</h2>
            <div class="text">
              <p>Computation is all around us. Whether it is the self-reinforcing machine learning algorithm used by the local police to predict a next crime, or whether it exists in the form of algorithmically created YouTube videos for children to maximize view counts or whether it is the next hype technology that consumes more electricity than entire countries. In today's information age, software not only becomes an essential part of everyday life, but it determines most of it. Nevertheless software’s implications are not always that visible or directly measurable. It seems like software is losing its materiality.</p>
                <p>Software itself is written in various programming languages, ranging from high-level languages that are close to English, to low-level languages like Assembly, which is also called symbolic machine code, because it is so near to writing actual 1s and 0s. Software essentially always compiles down to binary code to be able to execute on its underlying hardware. Today's hardware architecture is based on one crucial component called the transistor. It basically acts like a switch that can be turned on and off, or that sets the voltage to 5 or 0 Volts. This component made it possible to write basic logic in so called logic gates. A constellation of multiple logic gates is called an integrated circuit. It is build to perform specific kind of logic operations and from there on the complexity of logic is as good as infinite.</p>
                <p>Software is losing its local materiality, because since the beginning of computation it’s physicality has become increasingly smaller. In 1965 Gordon Moore, the founder of Intel, famously stated that the number of transistors in an integrated circuit will roughly double every two years. Since that statement the integrated circuit industry didn't slow down. Intel became so powerful that it drove almost every innovation in the field, leaving no space for competition while trying to catch up with its own expectations. The first transistor, made by Bell Labs, was roughly 50mm in height; nowadays transistor sizes down to 10 nanometers are the industrial standard (Beatrice Co, 2019).</p>
                <p>Physical computation became almost transparent: most of the computation is happening behind the user interface, embedded in integrated circuits consisting out of roughly 3 billion transistors, fiber-optic cables lay deep in the pacific, serving the invisible backbone of the internet, and almost everything is stored in the cloud, also known as a vast network of city sized data center spreading around the globe.</p>
                <p>But it is exactly these phenomenons of contradicting materiality, which make computation’s habitat: the almost transparent local materiality and its counterpart of a highly networked and wide-spread global materiality. Any closeby transistor has the potential to eventually influence and shift the formation of transistors on the other side of the planet and with that the outcome of its computational implications; Socio-political implications which create a specific kind of space; An interactive, dynamically linked space of conflict and ambiguity.</p><p>But it is also the contemporary artist working with computation, which is lost in between the machine and oneself. Since the rise of new media and silicon valley gadgets, computational thinking manifested itself into everyday life. Although the term is used frequently in and originated in the computer science industry, James Bridle, artist and author of New Dark Age, uses the term differently and puts it into another context. Rather than seeing computational thinking as the concept of being able to abstract a problem and automate and analyse the solution, Bridle connotes the term fairly sceptic. He sees “computational thinking [as] an extension [...to] solutionism: the belief that any given problem can be solved by the application of computation” (Bridle, 2018, p. 4) and argues that “computational thinking is predominant in the world today, driving the worst trends in our societies and interactions [...]” (Bridle, 2018, p. 4). Bridle goes as far as describing it as a way of thinking: “Computation replaces conscious thought. We think more and more like the machine, or we do not think at all” (Bridle, 2018, p. 43).</p><p>Not only do we see the world as a computational system, we actively constitute it as one. Through computation, this highly rationalistic way of thinking not only drives most disciplines from today, but it became the only option we have; Since “Les immateriaux” new media art was provided and sponsored by silicon valley tech companies (Quaranta, 2013, p. 61). With new media art building on highly rational, military structures to produce non-rational, emotional works, it was never quite certain if the exhibitions were actually critical of their media or just sales demonstrations of new technologies.</p><p>The silicon valley machine became the only option a new media artist has. But how can we act critical, when our way of thinking is so deeply rooted in what has to be criticized?</p><p>Maybe one could argue that the artist and the transistor are in a constant inhabitation of conflicting spaces, being ambiguous entities, always in between two states: their local materiality and their global socio-political implications.</p>
            </div>
          </div>
        </article>
      </main>
    </div>
    <div class="side collection">
      <video
        :src="`${base}visual/${visual}`"
        loop
        muted
        autoplay
      ></video>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'

export default {
  name: 'App',
  components: {},
  setup() {
    console.log(`%cf̷r̷a̷g̷m̷e̷n̷t̷a̷t̷i̷o̷n̷`, 'padding: 5px; background: blue; color: white;')

    const base = process.env.BASE_URL
    let materials = ref([])
    let updated = ref('')
    let notifications = ref([])
    let visual = ref('automation-surveillance.mp4')

    onMounted(async () => {
      /* const response = await fetch('http://localhost:2222/state')
      const json = await response.json()
      materials.value = json.material
      updated.value = json.updated */

      setInterval(() => {
        if (notifications.value.length > 0) return
        notifications.value.push('On the relationship between shiny interfaces and precarious work')
      }, 4000)
    })

    function removeNotification (content) {
      notifications.value = notifications.value.filter((n) => n !== content)
    }

    function changeVisual (path) {
      visual.value = path

    }

    function isVideo (file) {
      return file.endsWith('.mp4')
    }

    function onDrag (event) {
      console.log(event)
    }

    return {
      base,
      visual,
      materials,
      updated,
      notifications,
      removeNotification,
      changeVisual,
      isVideo,
      onDrag
    }
  }
}
</script>

<style>
::-webkit-scrollbar {
  width: 1em;
  height: 1em;
}

::-webkit-scrollbar-thumb {
  background: transparent;
  box-shadow: 0 0 1em rgba(255, 255, 255, 1);
}

@font-face {
  font-family: 'R';
  src: url('assets/fonts/r.otf') format('opentype');
  font-display: swap;
  font-weight: 300;
}

@font-face {
  font-family: 'S';
  src: url('assets/fonts/s.otf') format('opentype');
  font-display: swap;
  font-weight: 300;
}

* {
  box-sizing: border-box;
}

article *:not(img):not(video) {
  padding: 0.3em;
  border-radius: 100px;
  box-shadow: 0 0 0.1em rgba(255, 255, 255, 0.3);
}

html, body {
  margin: 0;
  padding: 0;
}

body {
  font-family: 'R', 'Arial', sans-serif;
  font-size: 16px;
  background: rgb(0, 0, 0);
  color: white;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
}

a, a:visited {
  color: silver;
  transition: 1s;
}

a:hover {
  text-shadow: 0 0 0.5em blue;
  filter: contrast(200);
}

p {
  margin: 0;
  margin-bottom: 1em;
}

h2 {
  margin: 0;
  margin-bottom: 1em;
  font-family: 'Arial', sans-serif;
  font-size: 1.3em;
  font-weight: normal;
  text-transform: uppercase;
  /* background: linear-gradient(to left, purple, black); */
}

small {
  display: block;
  font-size: 0.8em;
  line-height: 1;
}

img, video {
  display: block;
  max-width: 100%;
}

header {
  /* max-width: 700px; */
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1em 1.4em;
  background: transparent;
  color: silver;
  line-height: 1;
}

header a {
  text-decoration: none;
}

header p {
  margin: 0;
}

nav {
  text-align: right;
}

main {
  /* max-width: 700px; */
  padding: 1em;
}

article {
  display: flex;
  margin-bottom: 1em;
  gap: 1em;
}

article aside {
  flex: 0.333;
  cursor: crosshair;
  transition: 500ms;
  opacity: 1;
}

article aside:hover {
  flex: 1;
  opacity: 1;
}

article .content {
  flex: 1;
}

article .content .info {
  display: flex;
  justify-content: space-between;
  font-family: 'Arial', sans-serif;
  text-transform: uppercase;
  font-size: 1.2em;
}

article .content .text {
  line-height: 1.3;
  /* column-count: 2; */
}

.notifications {
  display: flex;
  flex-flow: column-reverse nowrap;
  align-items: flex-end;
  position: absolute;
  top: 1em;
  right: 1em;
  z-index: 5;
}

@keyframes slidein {
  from {
    transform: translateX(0.5em);
    opacity: 0;
  }

  to {
    transform: translateX(0px);
    opacity: 1;
  }
}

.notification {
  display: flex;
  margin: 0 0 0.5em 0;
  padding: 0.25em 0.5em;
  font-family: 'Arial', sans-serif;
  border: 1px solid;
  border-radius: 10px;
  user-select: none;
  animation-name: slidein;
  animation-duration: 1s;
  animation-iteration-count: 1;
}

.notification .x {
  margin: 0 0 0 1em;
  cursor: pointer;
}

.site {
  width: 100%;
  display: flex;
  gap: 1em;
}

.composition {
  flex: 1;
}

.collection {
  flex: 1.333;
}

.collection video {
  object-fit: fill;
  height: 100%;
  filter: blur(5px);
}

.side {
  height: 100vh;
  /* width: 100%; */
  overflow-y: scroll;
}

.source {
  margin-bottom: 1em;
  font-family: 'Arial', 'S', monospace;
  font-size: 0.7em;
}

.source a {
  text-transform: uppercase;
}

.source img {
  margin-bottom: 1em;
}

.pill {
  filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5em 1em;
  border-radius: 15px;
  text-shadow: 0 0 2em blue;
  background: transparent;
  cursor: pointer;
  /* transition: 10s; */
  filter: contrast(200);
}

.pill:hover {
  box-shadow: 0 0 2em blue;
}

/* .f {
  text-shadow: 0 0 0.5em blue;
  filter: contrast(200);
} */

.material {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  /* max-width: 300px; */
  /* box-shadow: 0 0 2em blue; */
}

.material > * {
  mix-blend-mode: multiply;
  display: block;
}
</style>
